import { createFileRoute } from '@tanstack/react-router';
import { useState, lazy, Suspense } from 'react';
import { AlertCircle, Plus } from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { PlanTable } from '@/components/plans';
import { usePlans, useCreatePlan, useUpdatePlan, useDeletePlan, useMoveSubscribers } from '@/hooks/usePlans';
import { usePermissions } from '@/hooks/usePermissions';
import type { SubscriptionPlan, CreateSubscriptionPlanRequest } from '@repo/types';

const PlanFormSheet = lazy(() =>
  import('@/components/plans/PlanFormSheet').then((m) => ({ default: m.PlanFormSheet }))
);
const DeletePlanDialog = lazy(() =>
  import('@/components/plans/DeletePlanDialog').then((m) => ({ default: m.DeletePlanDialog }))
);

export const Route = createFileRoute('/admin/plans/')({
  component: PlansPage,
});

function PlansPage() {
  const { hasAnyRole } = usePermissions();
  const canManage = hasAnyRole(['admin', 'manager']);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const { data, isLoading, error } = usePlans(page, pageSize);
  const createMutation = useCreatePlan();
  const updateMutation = useUpdatePlan();
  const deleteMutation = useDeletePlan();
  const moveMutation = useMoveSubscribers();

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleCreateClick = () => {
    setSelectedPlan(null);
    setFormOpen(true);
  };
  const handleEditClick = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setFormOpen(true);
  };
  const handleDeleteClick = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setDeleteOpen(true);
  };

  const handleSubmit = async (formData: CreateSubscriptionPlanRequest) => {
    try {
      if (selectedPlan) {
        await updateMutation.mutateAsync({ id: selectedPlan.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setFormOpen(false);
      setSelectedPlan(null);
    } catch {
      // Error toast is handled by the mutation hooks; keep the form open.
    }
  };

  /**
   * Step one when the plan still has customers. The dialog stays open — the plans
   * query refetches, the subscriber count drops to zero, and it becomes the delete
   * step for a separate, deliberate confirmation.
   */
  const handleMoveSubscribers = async (toPlanId: string) => {
    if (!selectedPlan) return;
    try {
      await moveMutation.mutateAsync({ id: selectedPlan.id, toPlanId });
    } catch {
      // Error toast is handled by the mutation hook; keep the dialog open.
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPlan) return;
    try {
      await deleteMutation.mutateAsync(selectedPlan.id);
      setDeleteOpen(false);
      setSelectedPlan(null);
    } catch {
      // Error toast is handled by the mutation hook; keep the dialog open.
    }
  };

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, data?.totalPages ?? 1);
  const rangeStart = total ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="space-y-6 p-6 duration-300 animate-in fade-in">
      <PageHeader
        title="Plans"
        description="Manage the subscription plan catalog, pricing and entitlements."
        actions={
          canManage ? (
            <Button size="sm" onClick={handleCreateClick}>
              <Plus className="size-4" />
              New Plan
            </Button>
          ) : undefined
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>Failed to load plans. Please try again.</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <DataTableSkeleton columns={5} />
      ) : (
        data && (
          <>
            <PlanTable
              data={data.data}
              canManage={canManage}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
            />

            {total > 0 && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-medium text-foreground">{rangeStart}</span>–
                  <span className="font-medium text-foreground">{rangeEnd}</span> of{' '}
                  <span className="font-medium text-foreground">{total}</span> plans
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="h-8 w-[72px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 20, 50, 100].map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}

      {/* Create / Edit form */}
      <Suspense fallback={null}>
        {formOpen && (
          <PlanFormSheet
            open={formOpen}
            onOpenChange={(open) => {
              setFormOpen(open);
              if (!open) setSelectedPlan(null);
            }}
            plan={selectedPlan}
            onSubmit={handleSubmit}
            isSubmitting={createMutation.isPending || updateMutation.isPending}
          />
        )}
      </Suspense>

      {/* Delete confirmation */}
      {selectedPlan && (
        <Suspense fallback={null}>
          {deleteOpen && (
            <DeletePlanDialog
              open={deleteOpen}
              onOpenChange={(open) => {
                setDeleteOpen(open);
                if (!open) setSelectedPlan(null);
              }}
              onConfirm={handleDeleteConfirm}
              onMoveSubscribers={handleMoveSubscribers}
              // The live row, not the snapshot taken when the dialog opened: after a
              // move the list refetches, and this is what drops the subscriber count
              // to zero so the dialog advances to its delete step.
              plan={data?.data.find((p) => p.id === selectedPlan.id) ?? selectedPlan}
              allPlans={data?.data ?? []}
              isDeleting={deleteMutation.isPending}
              isMoving={moveMutation.isPending}
            />
          )}
        </Suspense>
      )}
    </div>
  );
}
